import { useEffect, useMemo } from "react";
import classNames from "classnames";
import { Link } from "react-router-dom";
import { PageNav } from "@ugg/shared/components/common/PageNav";
import { AppRoutes, getChampionLeaderboard, getClassicChampionBuildUrl } from "@ugg/shared/routes/app-routes";
import { useClassicChampions, getClassicChampionImgUrl, resolveClassicAssetVersion } from "@ugg/shared/hooks/classic";
import { useUGGPatches } from "@ugg/shared/api/requests/patches";
import { usePageTitleAndDesc } from "@ugg/shared/utils/seo-helpers";

export function ClassicChampionHomePage({ className }: { className?: string }) {
  const getPageTitleAndDesc = usePageTitleAndDesc();
  const navTabs = [
    { to: AppRoutes.CHAMPION_HOME, label: "Champions", reset: true },
    { to: AppRoutes.CLASSIC_CHAMPIONS_HOME, label: "Classic Champions", reset: true },
    { to: getChampionLeaderboard(), label: "Leaderboards" },
  ];

  const championLinks = useClassicChampionLinks();

  useEffect(() => {
    window && window.scrollTo(0, 0);
  }, []);

  return (
    <div className={classNames("w-full", className)}>
      {getPageTitleAndDesc("classic_champions")}
      <div className="my-[24px]">
        <h1 className="font-barlow text-[36px] leading-[34px] font-semibold text-white">LoL Classic Champions Search</h1>
        <h2 className="mt-[12px] text-[18px] font-medium text-lavender-50">
          Discover the best builds for every classic champion
        </h2>
      </div>
      <PageNav tabs={navTabs} />
      <div
        className={`
        mt-[36px] grid gap-[18px] max-sm:gap-[12px]
        grid-cols-[repeat(auto-fill,minmax(100px,1fr))] [&>*]:max-w-[100px]
        max-sm:grid-cols-[repeat(auto-fill,minmax(80px,1fr))] max-sm:[&>*]:max-w-[80px]
        max-xs:grid-cols-[repeat(auto-fill,minmax(60px,1fr))] max-xs:[&>*]:max-w-[60px]
        p-[18px] max-sm:p-[12px] rounded-[3px] bg-purple-400
      `}
      >
        {championLinks}
      </div>
    </div>
  );
}

// Builds the grid tiles from the Classic champion manifest (custom cdragon
// source, not riot-assets). Mirrors the normal ChampionHome tile markup so the
// two grids read as one product, but portraits come from getClassicChampionImgUrl
// and links use the "/lol/champions/classic/:champion-classic" game-mode route.
// `ssr: true` prefetches the manifest during server render so the grid is
// server-rendered for SEO (the whole point of the standalone route).
function useClassicChampionLinks() {
  // This page has no patch filter, so the roster is always the latest patch's.
  // The Classic asset CDN is version-scoped, so the manifest and the portraits
  // both need that patch resolved before they can be fetched — `ssr: true` on
  // useUGGPatches keeps the whole chain server-rendered, which is the point of
  // the standalone route.
  const { data: uggPatches } = useUGGPatches({ ssr: true });
  const patch = uggPatches?.[0];
  const assetVersion = resolveClassicAssetVersion(patch);

  const { data: manifest } = useClassicChampions({ patch, ssr: true });

  const sortedChampions = useMemo(() => {
    return Object.entries(manifest || {})
      .map(([key, entry]) => ({
        key,
        // Coerce to strings: a malformed manifest entry (non-string in a slot)
        // must not throw on the .toLowerCase() sort below — empty/fallback wins.
        alias: typeof entry?.[0] === "string" ? entry[0] : "",
        name: typeof entry?.[1] === "string" && entry[1] ? entry[1] : `#${key}`,
      }))
      .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
  }, [manifest]);

  return useMemo(() => {
    return sortedChampions.map((champion) => (
      <Link className="group w-full mx-auto" key={champion.key} to={getClassicChampionBuildUrl(champion.alias)}>
        <div className="flex items-center justify-center aspect-square rounded-[3px] overflow-hidden">
          <div className="flex items-center justify-center flex-none w-[115%] overflow-hidden">
            {assetVersion && (
              <img
                className="w-full scale-[0.9] group-hover:scale-[.95] transition-[transform] duration-[250ms]"
                src={getClassicChampionImgUrl(champion.key, assetVersion)}
                alt={champion.name}
                loading="lazy"
              />
            )}
          </div>
        </div>
        <div className="group-hover:font-bold mt-[8px] text-[12px] text-white font-medium truncate">{champion.name}</div>
      </Link>
    ));
  }, [sortedChampions, assetVersion]);
}
